import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { errors, paxRoute, type RouteAuth, sessionRequired, staffAuth } from "../../common";
import {
  BalanceRequestHeaders,
  BalanceResponse,
  CreditHistoryQuery,
  CreditHistoryResponse,
  GameHoldsQuery,
  GameHoldsResponse,
  GameParams,
  PlayerOperationsQuery,
  PlayerOperationsResponse,
  UserParams,
} from "./schemas";

/** Session routes that read one player's credits when `userId` is theirs, or with `credits.view`. */
export const playerOrStaff: RouteAuth = {
  kind: "session",
  note: "Players read their own; reading another player's needs `credits.view`.",
};

const balanceOutput = z.union([
  z.object({
    status: z.literal(200).meta({ description: "The player's current credits." }),
    body: BalanceResponse,
  }),
  z.object({
    status: z.literal(304).meta({ description: "The credits have not changed since the ETag." }),
    headers: z.object({
      etag: z.string(),
    }),
  }),
]);

export const listPlayerHistory = oc
  .meta(
    paxRoute({ auth: playerOrStaff, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/accounting/v1alpha1/players/{userId}/history",
      operationId: "accounting.players.history.list",
      summary: "List a player's credit changes.",
      description:
        "Returns a page of the grants, charges, and removals that moved one player's balances, newest first, with the cursor for the next older page.",
      inputStructure: "detailed",
      successDescription: "One page of the player's credit changes.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    ...errors.forbidden,
  })
  .input(z.object({ params: UserParams, query: CreditHistoryQuery }))
  .output(CreditHistoryResponse);

export const getPlayerBalance = oc
  .meta(
    paxRoute({ auth: playerOrStaff, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/accounting/v1alpha1/players/{userId}/balances",
      operationId: "accounting.players.balances.get",
      summary: "Read a player's credits.",
      description:
        "Returns one player's eight balances, what their open holds still reserve, and whether a daily refresh can be claimed.",
      inputStructure: "detailed",
      outputStructure: "detailed",
      successDescription: "The player's credits.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    ...errors.forbidden,
    NOT_FOUND: { message: "No player has that id." },
  })
  .input(z.object({ params: UserParams, headers: BalanceRequestHeaders }))
  .output(balanceOutput);

export const listPlayerOperations = oc
  .meta(
    paxRoute({ auth: staffAuth("credits.view"), minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/accounting/v1alpha1/players/{userId}/operations",
      operationId: "accounting.players.operations.list",
      summary: "List a player's history.",
      description:
        "Returns a page of one player's history, newest first, back to when they signed up: the operations that moved their credits, and the holds they shared that were released with nothing charged. Each operation carries its entries and postings, and who made it and why when it was a support adjustment.",
      inputStructure: "detailed",
      successDescription: "One page of the player's history.",
    }),
  )
  .errors({ ...errors.badRequest, ...sessionRequired, ...errors.forbidden })
  .input(z.object({ params: UserParams, query: PlayerOperationsQuery }))
  .output(PlayerOperationsResponse);

export const listGameHolds = oc
  .meta(
    paxRoute({ auth: staffAuth("credits.view"), minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/accounting/v1alpha1/games/{gameId}/holds",
      operationId: "accounting.games.holds.list",
      summary: "List a game's holds.",
      description:
        "Returns a page of one game's holds, newest placed first, each with its kind, how it ended, who shared it, and the settlement for a settled hold. `outcome`, `from`, `to`, and `payerUserId` narrow the page.",
      inputStructure: "detailed",
      successDescription: "One page of the game's holds.",
    }),
  )
  .errors({ ...errors.badRequest, ...sessionRequired, ...errors.forbidden })
  .input(z.object({ params: GameParams, query: GameHoldsQuery }))
  .output(GameHoldsResponse);
